import { normalizeSportCode, sports } from './sports'
import { applyCeremonyTimeOverrides, isCeremonySession } from './ceremony-times'
import { add, addMinutes, differenceInHours, format, isSameDay, parse, parseISO, subMinutes } from 'date-fns'

export type EpgSchedule = {
    id: string
    description: string
    title: string
    isYesterday?: boolean
    since: string
    till: string
    channelUuid: string
    image: string
}

export type Channel = {
    uuid: string
    type?: string
    title: string
    country?: string
    provider?: number
    logo: string
    year?: number
}

export type EventDay = {
    logicalDay: number
    dateValue: string
    dateLabel: string
}

type Cg2026Event = {
    code: string
    description: string
    phaseDescription: string
    phaseLongDescription: string
    phasePrintDescription: string
    status: string
    genderCode: string
    order: number
    eventSortOrder: number
    startDate: string
    endDate: string
}

type Cg2026Session = {
    sessionCode: string
    sessionName: string
    disciplineCode: string
    disciplineDescription: string
    startDate: string
    endDate: string
    events: Cg2026Event[]
}

type Cg2026Day = {
    logicalDay: number
    sessions: Cg2026Session[]
}

type Cg2026ScheduleResponse = {
    generatedAt: string
    days: Cg2026Day[]
}

type ManualDateRange = {
    startDate?: string
    endDate?: string
}

type ScheduleData = {
    eventDays: EventDay[]
    sessionsByDate: Record<string, Cg2026Session[]>
}

type RuntimeConfig = {
    scheduleApiUrl?: string
    manualDateRange: ManualDateRange
}

type ChannelWithColor = Channel & {
    color: string | undefined
}

const DEFAULT_LOGO_URL =
    'https://raw.githubusercontent.com/Tapiosinn/tv-logos/master/countries/australia/seven-au.png'

const RUNTIME_CONFIG_URL = '/runtime-config.json'
const SERVICE_UNAVAILABLE_ERROR = 'Service unavailable. Please try again later.'
const DEFAULT_SESSION_DURATION_MS = 4 * 60 * 60 * 1000

export const shouldShowDetailedErrors = () => {
    if (typeof window === 'undefined') {
        return process.env.NODE_ENV !== 'production'
    }

    const host = window.location.hostname || ''
    return /(^|\.)localhost$|-pr\d+|\bdev\b/i.test(host)
}

const createServiceUnavailableError = (details?: string) => {
    if (shouldShowDetailedErrors() && details) {
        return new Error(`${SERVICE_UNAVAILABLE_ERROR} (${details})`)
    }

    return new Error(SERVICE_UNAVAILABLE_ERROR)
}

let scheduleDataPromise: Promise<ScheduleData> | null = null
let runtimeConfigPromise: Promise<RuntimeConfig> | null = null

const getRuntimeConfig = async (): Promise<RuntimeConfig> => {
    if (!runtimeConfigPromise) {
        runtimeConfigPromise = (async () => {
            try {
                const response = await fetch(RUNTIME_CONFIG_URL)
                if (!response.ok) {
                    return { manualDateRange: {} }
                }
                const data = (await response.json()) as RuntimeConfig
                return {
                    scheduleApiUrl: data.scheduleApiUrl,
                    manualDateRange: data.manualDateRange || {},
                }
            } catch {
                return { manualDateRange: {} }
            }
        })()
    }

    return runtimeConfigPromise
}

const getScheduleApiUrl = async () => {
    const runtimeConfig = await getRuntimeConfig()
    return runtimeConfig.scheduleApiUrl || null
}

const normalizeDateInput = (value: string) => {
    const normalized = value.replaceAll('/', '-')
    const parsed = safeParseMs(normalized)
    return parsed !== null ? normalized : null
}

const normalizeManualDateRange = (range: ManualDateRange | undefined): ManualDateRange => {
    if (!range) {
        return {}
    }

    const normalizedStartDate = range.startDate ? normalizeDateInput(range.startDate) : null
    const normalizedEndDate = range.endDate ? normalizeDateInput(range.endDate) : null

    return {
        startDate: normalizedStartDate || undefined,
        endDate: normalizedEndDate || undefined,
    }
}

const safeParseMs = (value: string) => {
    const ms = Date.parse(value)
    return Number.isFinite(ms) ? ms : null
}

const sortIsoDateStrings = (values: string[]) => {
    return values
        .filter((value) => safeParseMs(value) !== null)
        .sort((a, b) => {
            const aMs = safeParseMs(a) as number
            const bMs = safeParseMs(b) as number
            return aMs - bMs
        })
}

const formatIsoDateToWidgetDate = (value: string) => {
    return format(parseISO(value), 'yyyy/MM/dd')
}

const toWidgetDateKey = (value: string) => {
    return formatIsoDateToWidgetDate(value).replaceAll('/', '-')
}

const getSessionDescription = (session: Cg2026Session) => {
    const eventDescriptions = session.events
        .map((event) => event.description)
        .filter((description) => description && description.trim().length > 0)

    if (eventDescriptions.length > 0) {
        return eventDescriptions.slice(0, 3).join(' | ')
    }

    return session.sessionName || session.disciplineDescription
}

const normalizeSessionTimes = (session: Cg2026Session) => {
    const startMs = safeParseMs(session.startDate)
    const endMs = safeParseMs(session.endDate)

    if (startMs !== null && endMs !== null && endMs > startMs) {
        return {
            since: session.startDate,
            till: session.endDate,
        }
    }

    const eventStartDates = sortIsoDateStrings(session.events.map((event) => event.startDate))
    const eventEndDates = sortIsoDateStrings(session.events.map((event) => event.endDate))

    if (eventStartDates.length > 0 && eventEndDates.length > 0) {
        const since = eventStartDates[0]
        const till = eventEndDates[eventEndDates.length - 1]
        const sinceMs = safeParseMs(since)
        const tillMs = safeParseMs(till)

        if (sinceMs !== null && tillMs !== null && tillMs > sinceMs) {
            return { since, till }
        }

        if (sinceMs !== null) {
            return {
                since,
                till: new Date(sinceMs + DEFAULT_SESSION_DURATION_MS).toISOString(),
            }
        }
    }

    if (startMs !== null) {
        return {
            since: session.startDate,
            till: new Date(startMs + DEFAULT_SESSION_DURATION_MS).toISOString(),
        }
    }

    return {
        since: session.startDate,
        till: session.endDate,
    }
}

const sortEventDays = (eventDays: EventDay[]) => {
    return eventDays.sort((a, b) => {
        return (
            (safeParseMs(formatStartDate(a.dateValue)) as number) -
            (safeParseMs(formatStartDate(b.dateValue)) as number)
        )
    })
}

const createEventDay = (logicalDay: number, startDate: string): EventDay => {
    return {
        logicalDay,
        dateValue: formatIsoDateToWidgetDate(startDate),
        dateLabel: `Day ${logicalDay} - ${format(parseISO(startDate), 'MMM dd')}`,
    }
}

/**
 * Builds schedule data, keying non-ceremony sessions by the logical day's earliest start
 * and ceremony sessions by their own local start date so AU viewers still get a day tab.
 */
export const buildCg2026ScheduleData = (schedule: Cg2026ScheduleResponse): ScheduleData => {
    const sessionsByDate: Record<string, Cg2026Session[]> = {}
    const eventDaysByDateKey = new Map<string, EventDay>()
    let maxLogicalDay = schedule.days.reduce((max, day) => Math.max(max, day.logicalDay), 0)

    const appendSessions = (dateKey: string, sessions: Cg2026Session[]) => {
        if (sessions.length === 0) {
            return
        }
        sessionsByDate[dateKey] = (sessionsByDate[dateKey] || []).concat(sessions)
    }

    // Pass 1: non-ceremony sessions define the primary day tabs.
    schedule.days.forEach((day) => {
        const nonCeremonySessions = day.sessions.filter((session) => !isCeremonySession(session))
        if (nonCeremonySessions.length === 0) {
            return
        }

        const sortedSessionStartDates = sortIsoDateStrings(
            nonCeremonySessions.map((session) => session.startDate),
        )
        if (sortedSessionStartDates.length === 0) {
            return
        }

        const dayStartDate = sortedSessionStartDates[0]
        const dayKey = toWidgetDateKey(dayStartDate)
        appendSessions(dayKey, nonCeremonySessions)

        if (!eventDaysByDateKey.has(dayKey)) {
            eventDaysByDateKey.set(dayKey, createEventDay(day.logicalDay, dayStartDate))
        }
    })

    // Pass 2: ceremonies key by their own local start date (merge or add trailing day).
    schedule.days.forEach((day) => {
        const ceremonySessions = day.sessions.filter((session) => isCeremonySession(session))

        ceremonySessions.forEach((session) => {
            const ceremonyKey = toWidgetDateKey(session.startDate)
            appendSessions(ceremonyKey, [session])

            if (!eventDaysByDateKey.has(ceremonyKey)) {
                maxLogicalDay += 1
                eventDaysByDateKey.set(ceremonyKey, createEventDay(maxLogicalDay, session.startDate))
            }
        })
    })

    return {
        eventDays: sortEventDays(Array.from(eventDaysByDateKey.values())),
        sessionsByDate,
    }
}

export const applyManualDateRangeToScheduleData = (
    scheduleData: ScheduleData,
    range: ManualDateRange = {},
) => {
    const normalizedRange = normalizeManualDateRange(range)
    if (!normalizedRange.startDate && !normalizedRange.endDate) {
        return scheduleData
    }

    const startMs = normalizedRange.startDate ? safeParseMs(normalizedRange.startDate) : null
    const endMs = normalizedRange.endDate ? safeParseMs(normalizedRange.endDate) : null

    const dateKeyHasCeremony = (dateKey: string) =>
        (scheduleData.sessionsByDate[dateKey] || []).some((session) => isCeremonySession(session))

    const filteredEventDays = scheduleData.eventDays.filter((eventDay) => {
        const dateKey = formatStartDate(eventDay.dateValue)
        const dateMs = safeParseMs(dateKey)
        if (dateMs === null) {
            return false
        }

        const isAfterStart = startMs === null || dateMs >= startMs
        const isBeforeEnd = endMs === null || dateMs <= endMs
        if (isAfterStart && isBeforeEnd) {
            return true
        }

        // Keep ceremony spillover tabs (e.g. Aug 03 for AU closing) outside the configured range.
        return dateKeyHasCeremony(dateKey)
    })

    const availableDateKeys = new Set(
        filteredEventDays.map((eventDay) => formatStartDate(eventDay.dateValue)),
    )

    const filteredSessionsByDate = Object.entries(scheduleData.sessionsByDate).reduce(
        (acc, [dateKey, sessions]) => {
            if (availableDateKeys.has(dateKey)) {
                acc[dateKey] = sessions
            }
            return acc
        },
        {} as Record<string, Cg2026Session[]>,
    )

    return {
        eventDays: filteredEventDays,
        sessionsByDate: filteredSessionsByDate,
    }
}

const fetchCg2026ScheduleData = async (): Promise<ScheduleData> => {
    const scheduleApiUrl = await getScheduleApiUrl()
    if (!scheduleApiUrl) {
        throw createServiceUnavailableError('scheduleApiUrl is missing in runtime config')
    }

    try {
        const response = await fetch(scheduleApiUrl)
        if (!response.ok) {
            throw createServiceUnavailableError(`Schedule API request failed with status ${response.status}`)
        }

        const schedulePayload = (await response.json()) as Cg2026ScheduleResponse
        if (!schedulePayload.days || !Array.isArray(schedulePayload.days)) {
            throw createServiceUnavailableError('Schedule payload is missing days[]')
        }

        const schedule = applyCeremonyTimeOverrides(schedulePayload)
        return buildCg2026ScheduleData(schedule)
    } catch (error) {
        const details = error instanceof Error ? error.message : 'Unknown fetch/parse error'
        throw createServiceUnavailableError(details)
    }
}

const getScheduleData = async (): Promise<ScheduleData> => {
    if (!scheduleDataPromise) {
        scheduleDataPromise = (async () => {
            const fetchedScheduleData = await fetchCg2026ScheduleData()
            const runtimeConfig = await getRuntimeConfig()
            const filteredScheduleData = applyManualDateRangeToScheduleData(
                fetchedScheduleData,
                runtimeConfig.manualDateRange || {},
            )

            if (filteredScheduleData.eventDays.length === 0) {
                throw createServiceUnavailableError(
                    'No event days available after applying manualDateRange filter',
                )
            }

            return filteredScheduleData
        })().catch((error) => {
            scheduleDataPromise = null
            throw error
        })
    }

    return scheduleDataPromise
}

// Creates channels(sports) to be displayed in the EPG
const extractDisciplinesFromSessionData = (sessions: Cg2026Session[]): ChannelWithColor[] => {
    const disciplinesAsChannels: ChannelWithColor[] = []
    const seenDisciplines = new Set<string>()

    sessions.forEach((sessionData) => {
        const normalizedDisciplineCode = normalizeSportCode(sessionData.disciplineCode)

        if (seenDisciplines.has(normalizedDisciplineCode)) {
            return
        }
        seenDisciplines.add(normalizedDisciplineCode)

        // find sport with code that matches the currently processed data
        const sportWithLogo = sports.find((sport) => sport.code === normalizedDisciplineCode)
        // create channel object and add to array for returning for schedule
        disciplinesAsChannels.push({
            uuid: normalizedDisciplineCode,
            title: sessionData.disciplineDescription,
            logo: sportWithLogo?.logo || DEFAULT_LOGO_URL,
            color: sportWithLogo?.color,
        })
    })

    return disciplinesAsChannels
}

const extractEpgFromSessionData = (sessions: Cg2026Session[]): EpgSchedule[] => {
    const sessionsAsEpg: EpgSchedule[] = sessions.map((sessionData) => {
        const normalizedSessionTime = normalizeSessionTimes(sessionData)
        const normalizedDisciplineCode = normalizeSportCode(sessionData.disciplineCode)

        return {
            id: sessionData.sessionCode,
            channelUuid: normalizedDisciplineCode,
            title: sessionData.disciplineDescription,
            description: getSessionDescription(sessionData),
            since: normalizedSessionTime.since,
            till: normalizedSessionTime.till,
            image: DEFAULT_LOGO_URL,
        }
    })
    return sessionsAsEpg
}

const getSessionsForTodayAndCarriedOverFromYesterday = (
    startDate: string,
    sessionsByDate: Record<string, Cg2026Session[]>,
) => {
    const dateFromString = parseISO(startDate)
    const yesterday = add(dateFromString, { days: -1 })
    const sessions = sessionsByDate[startDate] || []

    // filter out events from today that are actually tomorrow due to timezone differences
    const sessionsTodayFiltered = sessions.filter((el: Cg2026Session) => {
        const eventStartDate = new Date(el.startDate)

        const eventStartDateFixed = addMinutes(eventStartDate, 1)
        return (
            isSameDay(dateFromString, eventStartDateFixed) &&
            differenceInHours(eventStartDateFixed, dateFromString) <= 25
        )
    })
    const sessionsYesterday = sessionsByDate[format(yesterday, TIME_FORMAT.DATE)] || []

    // filter out events from yesterday, so array only has events ending in current selected day
    const sessionsYesterdayFiltered = sessionsYesterday.filter((el: Cg2026Session) => {
        const normalizedSessionTime = normalizeSessionTimes(el)
        const eventEndDate = new Date(normalizedSessionTime.till)

        // fix for bug if event ends 12:00am midnight it will be treated as if it's also part of next day (causes empty row for that sport to appear the next day)
        const eventEndDateFixed = subMinutes(eventEndDate, 1)

        return isSameDay(dateFromString, eventEndDateFixed)
    })

    const sessionsTodayYesterday = sessionsTodayFiltered.concat(sessionsYesterdayFiltered)
    return sessionsTodayYesterday
}

// API Url format: https://api.birmingham2022.com/cwg-schedule/v1/sessions?sessionDate=2022-08-05&size=200
export const fetchChannels = async (startDate: string): Promise<Channel[]> => {
    const scheduleData = await getScheduleData()
    const sessionsTodayYesterday = getSessionsForTodayAndCarriedOverFromYesterday(
        startDate,
        scheduleData.sessionsByDate,
    )
    const channels = await extractDisciplinesFromSessionData(sessionsTodayYesterday)
    return channels
}

export const fetchEpg = async (startDate: string): Promise<EpgSchedule[]> => {
    const scheduleData = await getScheduleData()
    const sessionsTodayYesterday = getSessionsForTodayAndCarriedOverFromYesterday(
        startDate,
        scheduleData.sessionsByDate,
    )
    const sessions = await extractEpgFromSessionData(sessionsTodayYesterday)
    return sessions
}

export const fetchEventDays = async (): Promise<EventDay[]> => {
    const scheduleData = await getScheduleData()
    return scheduleData.eventDays
}

export const TIME_FORMAT = {
    DATE: 'yyyy-MM-dd',
    HOURS_MIN: 'HH:mm',
    BASE_HOURS_TIME: 'h:mm a',
}

export const formatTimeInto12Hours = (time: string) => {
    const currentDate = new Date()
    const baseDateFormatted = format(currentDate, TIME_FORMAT.DATE)
    const baseDateWithTime = new Date(`${baseDateFormatted}T${time}:00`)
    const timeFormat = format(baseDateWithTime, TIME_FORMAT.BASE_HOURS_TIME)
    return timeFormat.toLowerCase().replace(/\s/g, '')
}

// formats "2022/08/05" into "2022-08-05" (ISO date format)
export const formatStartDate = (startDate: string) => {
    return startDate.replaceAll('/', '-')
}

export const isMobileScreenSize = () => {
    const screenWidth = window.screen.width * window.devicePixelRatio
    if (screenWidth <= 768 * window.devicePixelRatio) {
        return true
    } else {
        return false
    }
}

export type Placement = '7news' | '7plus'

export const getPlacement = (): Placement => {
    const urlParams = new URLSearchParams(window.location.search)
    const placement = urlParams.get('placement') || '7plus'
    if (placement === '7news') {
        return '7news'
    } else {
        return '7plus'
    }
}

export const getTimeOfFirstEvent = (events: EpgSchedule[], currentSelectedDate: string) => {
    events.sort((eventA: EpgSchedule, eventB: EpgSchedule) => {
        const dateA = new Date(eventA.since)
        const dateB = new Date(eventB.since)
        return dateA.getTime() - dateB.getTime()
    })
    if (!isSameDay(parseISO(formatStartDate(currentSelectedDate)), new Date(events[0].since))) {
        // return start of current selected day if first event is carried over from previous day
        return `${formatStartDate(currentSelectedDate)}T00:00:00`
    }
    return events[0].since
}

export const sortChannels = (channels: Channel[]) => {
    // move opening ceremony to beginning of array
    const index = channels.findIndex((el) => {
        return el.uuid === 'CER'
    })
    if (index && index > 0) {
        const element = channels.splice(index, 1)[0]
        channels.splice(0, 0, element)
    }
    return channels
}

const getFillerBlock = (
    channelCode: string,
    startTime: string,
    endTime: string,
    isFirstFillerBlock: boolean = false,
) => {
    if (isFirstFillerBlock) {
        // fix to make filler blocks start visually at the same position as events caried over from previous day
        const adjustedStartTime = subMinutes(parseISO(startTime), 5)
        startTime = adjustedStartTime.toISOString()
    }
    const filler: EpgSchedule = {
        id: 'filler' + channelCode + startTime,
        title: 'filler',
        description: 'filler',
        channelUuid: channelCode,
        image: '',
        since: startTime,
        till: endTime,
    }
    return filler
}

export const addFillerBlocks = (programs: EpgSchedule[], channels: Channel[], eventDate: string) => {
    // get schedule start and end in current timezone
    const midnightStartOfDay = parse(`${eventDate} 12:01am`, 'yyyy-MM-dd h:mma', new Date())
    const midnightEndOfDay = parse(`${eventDate} 11:59pm`, 'yyyy-MM-dd h:mma', new Date())

    // add filler block for each channel
    channels.forEach((channel) => {
        const eventsForCurrentSportFiltered = programs.filter((el) => el.channelUuid === channel.uuid)
        const eventsForCurrentSport = eventsForCurrentSportFiltered.sort((eventA, eventB) => {
            const dateA = parseISO(eventA.since)
            const dateB = parseISO(eventB.since)
            if (dateA.getTime() > dateB.getTime()) {
                return 1
            } else {
                return -1
            }
        })

        if (eventsForCurrentSport.length === 1) {
            // add filler block before and after
            const firstFiller = getFillerBlock(
                channel.uuid,
                midnightStartOfDay.toISOString(),
                eventsForCurrentSport[0].since,
                true,
            )
            programs.push(firstFiller)
            const lastFiller = getFillerBlock(
                channel.uuid,
                eventsForCurrentSport[0].till,
                midnightEndOfDay.toISOString(),
            )
            programs.push(lastFiller)
        } else if (eventsForCurrentSport.length === 2) {
            // add filler block before first event
            const firstFiller = getFillerBlock(
                channel.uuid,
                midnightStartOfDay.toISOString(),
                eventsForCurrentSport[0].since,
                true,
            )
            programs.push(firstFiller)
            // add filler block between events
            const secondFiller = getFillerBlock(
                channel.uuid,
                eventsForCurrentSport[0].till,
                eventsForCurrentSport[1].since,
            )
            programs.push(secondFiller)
            // add filler block after second event
            const thirdFiller = getFillerBlock(
                channel.uuid,
                eventsForCurrentSport[1].till,
                midnightEndOfDay.toISOString(),
            )
            programs.push(thirdFiller)
        } else if (eventsForCurrentSport.length === 3) {
            // add filler block before first event
            const firstFiller = getFillerBlock(
                channel.uuid,
                midnightStartOfDay.toISOString(),
                eventsForCurrentSport[0].since,
                true,
            )
            programs.push(firstFiller)
            // add filler block between events
            const secondFiller = getFillerBlock(
                channel.uuid,
                eventsForCurrentSport[0].till,
                eventsForCurrentSport[1].since,
            )
            programs.push(secondFiller)
            // add filler block between events
            const thirdFiller = getFillerBlock(
                channel.uuid,
                eventsForCurrentSport[1].till,
                eventsForCurrentSport[2].since,
            )
            programs.push(thirdFiller)
            // add filler block after third event
            const fourthFiller = getFillerBlock(
                channel.uuid,
                eventsForCurrentSport[2].till,
                midnightEndOfDay.toISOString(),
            )
            programs.push(fourthFiller)
        } else if (eventsForCurrentSport.length === 4) {
            // add filler block before first event
            const firstFiller = getFillerBlock(
                channel.uuid,
                midnightStartOfDay.toISOString(),
                eventsForCurrentSport[0].since,
                true,
            )
            programs.push(firstFiller)
            // add filler block between events
            const secondFiller = getFillerBlock(
                channel.uuid,
                eventsForCurrentSport[0].till,
                eventsForCurrentSport[1].since,
            )
            programs.push(secondFiller)
            // add filler block between events
            const thirdFiller = getFillerBlock(
                channel.uuid,
                eventsForCurrentSport[1].till,
                eventsForCurrentSport[2].since,
            )
            programs.push(thirdFiller)
            // add filler block between events
            const fourthFiller = getFillerBlock(
                channel.uuid,
                eventsForCurrentSport[2].till,
                eventsForCurrentSport[3].since,
            )
            programs.push(fourthFiller)
            // add filler block after last event
            const fifthFiller = getFillerBlock(
                channel.uuid,
                eventsForCurrentSport[3].till,
                midnightEndOfDay.toISOString(),
            )
            programs.push(fifthFiller)
        } else if (eventsForCurrentSport.length === 5) {
            // add filler block before first event
            const firstFiller = getFillerBlock(
                channel.uuid,
                midnightStartOfDay.toISOString(),
                eventsForCurrentSport[0].since,
                true,
            )
            programs.push(firstFiller)
            // add filler block between events
            const secondFiller = getFillerBlock(
                channel.uuid,
                eventsForCurrentSport[0].till,
                eventsForCurrentSport[1].since,
            )
            programs.push(secondFiller)
            const thirdFiller = getFillerBlock(
                channel.uuid,
                eventsForCurrentSport[1].till,
                eventsForCurrentSport[2].since,
            )
            programs.push(thirdFiller)
            const fourthFiller = getFillerBlock(
                channel.uuid,
                eventsForCurrentSport[2].till,
                eventsForCurrentSport[3].since,
            )
            programs.push(fourthFiller)
            const fifthFiller = getFillerBlock(
                channel.uuid,
                eventsForCurrentSport[3].till,
                eventsForCurrentSport[4].since,
            )
            programs.push(fifthFiller)
            // add filler block after last event
            const sixthFiller = getFillerBlock(
                channel.uuid,
                eventsForCurrentSport[4].till,
                midnightEndOfDay.toISOString(),
            )
            programs.push(sixthFiller)
        }
    })

    return programs
}
